/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package repository

import io.ktor.client.HttpClient
import io.ktor.client.plugins.websocket.WebSockets
import io.ktor.client.plugins.websocket.webSocket
import io.ktor.websocket.Frame
import io.ktor.websocket.readText
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.cancelAndJoin
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

/**
 * WebSocket message from server for goal synchronization
 */
@Serializable
data class GoalSyncMessage(
    val type: String, // "goal_created", "goal_updated", "goal_deleted", "progress_updated", "connected"
    val userId: String,
    val goalId: String,
    val data: String? = null,
)

/**
 * Callback interface for handling WebSocket goal sync events
 */
interface GoalSyncListener {
    suspend fun onGoalCreated(goalId: String, data: String?)
    suspend fun onGoalUpdated(goalId: String, data: String?)
    suspend fun onGoalDeleted(goalId: String)
    suspend fun onProgressUpdated(goalId: String, currentAmount: String?)
    suspend fun onConnected()
    suspend fun onDisconnected()
}

/**
 * WebSocket client for real-time goal synchronization
 * Connects to server's /ws/goals/{userId} endpoint and listens for updates
 */
class GoalsSyncWebSocketClient(
    private val serverUrl: String, // e.g., "ws://localhost:3000" or "ws://10.0.2.2:3000" for Android emulator
    private val userId: String,
    private val listener: GoalSyncListener,
    // Server runs in soft-auth mode (Option B): if a token is supplied we
    // verify it and require its `sub` == userId; if absent we still accept
    // the legacy unauthenticated connect. Web is wiring this up first; iOS
    // and Android stay on the legacy path until their next release, then we
    // flip REQUIRE_WS_AUTH on the VPS.
    // TODO(ws-auth-phase-2): drop the default once all clients pass a token.
    private val getAuthToken: (suspend () -> String?)? = null,
) {
    private val client = HttpClient {
        install(WebSockets)
    }

    private val scope = CoroutineScope(Dispatchers.Default)
    private var connectionJob: Job? = null
    private var isConnecting = false

    /** Once the server closes with code 1008 (Policy Violation — used by
     *  the goals WS handler for "Invalid token" / "Token does not match
     *  userId"), STOP retrying. Reconnect-with-same-expired-token is a
     *  busy-loop that floods the server log with one rejection per
     *  second indefinitely. Cleared when the caller explicitly calls
     *  connect() again (typically after a fresh login → new client
     *  instance from the factory, since the factory rebuilds the repo
     *  whenever userId changes). */
    private var authBlocked = false

    /**
     * Start WebSocket connection and listen for updates
     * Automatically reconnects on disconnection
     */
    fun connect() {
        if (connectionJob?.isActive == true) {
            println("🔌 WebSocket: Already connected/connecting, skipping duplicate connect")
            return
        }
        if (authBlocked) {
            println("⛔ WebSocket: Previously stopped by server auth-reject (1008). Not reconnecting until a fresh client is created (typically post re-login).")
            return
        }

        isConnecting = true
        connectionJob = scope.launch {
            var reconnectDelay = 1000L // Start with 1 second

            while (isActive && !authBlocked) {
                // Captured inside webSocket{} so we can inspect the
                // server's close reason after the session ends.
                var lastCloseCode: Short? = null
                try {
                    val token = getAuthToken?.invoke()
                    val url =
                        if (token.isNullOrBlank()) {
                            "$serverUrl/ws/goals/$userId"
                        } else {
                            "$serverUrl/ws/goals/$userId?token=$token"
                        }
                    println("🔌 WebSocket: Connecting to $serverUrl/ws/goals/$userId (auth=${if (token.isNullOrBlank()) "legacy" else "token"})")
                    client.webSocket(url) {
                        println("✅ WebSocket: Connected successfully")
                        isConnecting = false
                        reconnectDelay = 1000L // Reset delay on successful connection

                        try {
                            listener.onConnected()

                            // Listen for incoming messages
                            for (frame in incoming) {
                                if (frame is Frame.Text) {
                                    try {
                                        val text = frame.readText()
                                        handleMessage(text)
                                    } catch (e: Exception) {
                                        println("⚠️ WebSocket: Error handling message: ${e.message}")
                                        e.printStackTrace()
                                        // Don't break connection on message handling error
                                    }
                                }
                            }
                        } catch (e: Exception) {
                            println("⚠️ WebSocket: Error in message loop: ${e.message}")
                            e.printStackTrace()
                        }
                        // Capture the server's close reason BEFORE this
                        // block exits — outside the block, the session
                        // is gone and the deferred can no longer resolve.
                        lastCloseCode = closeReason.await()?.code
                    }
                    println("🔌 WebSocket: Connection closed gracefully (code=${lastCloseCode ?: "n/a"})")
                    listener.onDisconnected()

                    // 1008 = Policy Violation. The server's goals WS handler
                    // uses it for every auth-reject path (expired token,
                    // invalid token, token-subject != path-userId, missing
                    // token when REQUIRE_WS_AUTH=true). All are unrecoverable
                    // without fresh credentials, so stop the retry loop
                    // instead of hammering the server.
                    if (lastCloseCode?.toInt() == 1008) {
                        println("🔒 WebSocket: Server rejected auth (1008). Stopping reconnect loop until next connect() call.")
                        authBlocked = true
                        return@launch
                    }

                    // Reconnect after graceful close
                    println("🔄 WebSocket: Reconnecting in ${reconnectDelay / 1000} seconds...")
                    delay(reconnectDelay)
                    reconnectDelay = minOf(reconnectDelay * 2, 30000L)
                } catch (e: Exception) {
                    println("❌ WebSocket: Connection error: ${e.message}")
                    e.printStackTrace()
                    isConnecting = false
                    listener.onDisconnected()

                    // Exponential backoff for reconnection (max 30 seconds)
                    println("🔄 WebSocket: Reconnecting in ${reconnectDelay / 1000} seconds...")
                    delay(reconnectDelay)
                    reconnectDelay = minOf(reconnectDelay * 2, 30000L)
                }
            }
        }
    }

    /**
     * Pause WebSocket — cancel the connection job but keep the HttpClient alive
     * so connect() can be called again later (e.g., when returning from background).
     */
    fun pauseConnection() {
        println("⏸️ WebSocket: Pausing connection...")
        connectionJob?.cancel()
        connectionJob = null
        isConnecting = false
    }

    /**
     * Permanently close WebSocket connection AND the underlying HttpClient.
     * After this, the client cannot be reused.
     */
    suspend fun disconnect() {
        println("🔌 WebSocket: Disconnecting permanently...")
        connectionJob?.cancelAndJoin()
        connectionJob = null
        isConnecting = false
        client.close()
    }

    /**
     * Handle incoming WebSocket message from server
     */
    private suspend fun handleMessage(text: String) {
        try {
            val message = Json.decodeFromString<GoalSyncMessage>(text)
            println("📥 WebSocket: Received ${message.type} for goal ${message.goalId}")

            when (message.type) {
                "connected" -> {
                    listener.onConnected()
                }
                "goal_created" -> {
                    listener.onGoalCreated(message.goalId, message.data)
                }
                "goal_updated" -> {
                    listener.onGoalUpdated(message.goalId, message.data)
                }
                "goal_deleted" -> {
                    listener.onGoalDeleted(message.goalId)
                }
                "progress_updated" -> {
                    listener.onProgressUpdated(message.goalId, message.data)
                }
                else -> {
                    println("⚠️ WebSocket: Unknown message type: ${message.type}")
                }
            }
        } catch (e: Exception) {
            println("❌ WebSocket: Failed to parse message: ${e.message}")
        }
    }
}
